// dashboard/frontend/src/components/Navigation/Navigation.js
import React, { useState } from 'react';
import { ROUTES, isAdmin } from '../../App';
import { 
    AppBar, 
    Toolbar, 
    Typography, 
    Button, 
    Box,
    Link,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    useTheme,
    useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PricingDialog from '../Pricing/PricingDialog';

function Navigation() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [showPricing, setShowPricing] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleLogout = async () => {
        await logout();
        navigate('/auth/login');
        setMobileMenuOpen(false);
    };

    const getNavItems = () => {
        if (!user) return [];

        return ROUTES.protected
            .filter(route => {
                if (!route.showInNav) return false;
                if (route.adminOnly && !isAdmin(user)) return false;
                return true;
            })
            .sort((a, b) => a.order - b.order)
            .map(route => ({
                to: route.path,
                label: route.title
            }));
    };

    const handleGetStarted = () => {
        if (user) {
            navigate('/dashboard');
        } else {
            setShowPricing(true);
        }
        setMobileMenuOpen(false);
    };

    const isPublicRoute = () => {
        return ROUTES.public.some(route => location.pathname === route.path) ||
               location.pathname === '/terms' ||
               location.pathname === '/privacy' ||
               location.pathname === '/contact';
    };

    const MobileDrawer = () => (
        <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
        >
            <Box sx={{ width: 250, pt: 2 }}>
                {isPublicRoute() ? (
                    <List>
                        {!user ? (
                            <>
                                <ListItem 
                                    button 
                                    onClick={() => {
                                        navigate('/auth/login');
                                        setMobileMenuOpen(false);
                                    }}
                                >
                                    <ListItemText primary="Login" />
                                </ListItem>
                                <ListItem 
                                    button 
                                    onClick={handleGetStarted}
                                >
                                    <ListItemText primary="Get Started" />
                                </ListItem>
                            </>
                        ) : (
                            <ListItem 
                                button 
                                onClick={() => {
                                    navigate('/dashboard');
                                    setMobileMenuOpen(false);
                                }}
                            >
                                <ListItemText primary="Go to Dashboard" />
                            </ListItem>
                        )}
                    </List>
                ) : (
                    user && (
                        <List>
                            {getNavItems().map(item => (
                                <ListItem 
                                    key={item.to}
                                    button
                                    component={NavLink}
                                    to={item.to}
                                    onClick={() => setMobileMenuOpen(false)}
                                    sx={{
                                        '&.active': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.08)'
                                        }
                                    }}
                                >
                                    <ListItemText primary={item.label} />
                                </ListItem>
                            ))}
                            <ListItem button onClick={handleLogout}>
                                <ListItemText primary="Logout" />
                            </ListItem>
                        </List>
                    )
                )}
            </Box>
        </Drawer>
    );

    return (
        <>
            <AppBar 
                position="fixed"
                sx={{
                    backdropFilter: 'blur(5px)',
                    boxShadow: (theme) => `0 0 10px ${theme.palette.grey[300]}`
                }}
            >
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        <Link 
                            component={NavLink} 
                            to="/" 
                            color="inherit" 
                            sx={{ 
                                textDecoration: 'none',
                                '&:hover': {
                                    color: 'rgba(255, 255, 255, 0.8)'
                                }
                            }}
                        >
                            {window.location.hostname.includes('wpappdev.com') ? 'ArgusSTAGING' : 'ArgusCMS'}
                        </Link>
                    </Typography>

                    {isMobile ? (
                        <IconButton
                            color="inherit"
                            onClick={() => setMobileMenuOpen(true)}
                            edge="end"
                        >
                            <MenuIcon />
                        </IconButton>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {isPublicRoute() ? (
                                !user ? (
                                    <>
                                        <Button 
                                            color="inherit"
                                            onClick={() => navigate('/auth/login')}
                                        >
                                            Login
                                        </Button>
                                        <Button 
                                            variant="contained"
                                            color="secondary"
                                            onClick={handleGetStarted}
                                            sx={{
                                                backgroundColor: 'white',
                                                color: 'primary.main',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                                                }
                                            }}
                                        >
                                            Get Started
                                        </Button>
                                    </>
                                ) : (
                                    <Button 
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => navigate('/dashboard')}
                                        sx={{
                                            backgroundColor: 'white',
                                            color: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)'
                                            }
                                        }}
                                    >
                                        Go to Dashboard
                                    </Button>
                                )
                            ) : (
                                user && (
                                    <>
                                        {getNavItems().map(button => (
                                            <Button 
                                                key={button.to}
                                                color="inherit" 
                                                component={NavLink} 
                                                to={button.to}
                                                sx={{
                                                    '&.active': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.12)'
                                                    }
                                                }}
                                            >
                                                {button.label}
                                            </Button>
                                        ))}
                                        <Button 
                                            color="inherit"
                                            onClick={handleLogout}
                                        >
                                            Logout
                                        </Button>
                                    </>
                                )
                            )}
                        </Box>
                    )}
                </Toolbar>
            </AppBar>
            <Toolbar />
            <MobileDrawer />
            <PricingDialog 
                open={showPricing} 
                onClose={() => setShowPricing(false)} 
            />
        </>
    );
}

export default Navigation;